import React, { useState } from 'react';
import { Alert, Box, Typography, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IMAPSource, SMTPSource } from '../../models/email-service';
import { PrimaryButton } from '../buttons';
import { Body1 } from '../typography';

interface EmailIntegrationProps {
  /** Whether this is for tenant or user */
  type: 'tenant' | 'user';
  /** Current SMTP source */
  smtpSource?: SMTPSource;
  /** Current IMAP source */
  imapSource?: IMAPSource;
  /** Email address being used */
  emailAddress?: string;
  /** Function to link email account */
  onLinkEmail: (provider: 'gmail' | 'office365') => Promise<void>;
  /** Optional description text */
  description?: string;
  /** Email signature plain text value */
  emailSignaturePlain?: string;
  /** Email signature HTML value */
  emailSignatureHtml?: string;
  /** Change handler for plain text signature */
  onChangeEmailSignaturePlain?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Change handler for HTML signature */
  onChangeEmailSignatureHtml?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Optional children for additional form fields (e.g., IMAP/SMTP settings) */
  children?: React.ReactNode;
}

const EmailIntegration: React.FC<EmailIntegrationProps> = ({
  type,
  smtpSource,
  imapSource,
  emailAddress,
  onLinkEmail,
  description,
  emailSignaturePlain,
  emailSignatureHtml,
  onChangeEmailSignaturePlain,
  onChangeEmailSignatureHtml,
  children
}) => {
  const { t } = useTranslation();

  const defaultDescription = type === 'tenant'
    ? t('Connect your email accounts to enable seamless communication within the platform. You can link Gmail or Office 365 accounts for sending and receiving emails directly.')
    : t('Configure your email settings below to enable email retrieval and management through your preferred email service provider.');

  // Helper to get provider name
  const getProviderName = (source: SMTPSource | IMAPSource): string => {
    switch (source) {
      case SMTPSource.GMAIL:
      case IMAPSource.GMAIL:
        return 'Gmail';
      case SMTPSource.OFFICE365:
      case IMAPSource.OFFICE365:
        return 'Office 365';
      case SMTPSource.AWS_SES:
        return 'AWS SES';
      case SMTPSource.SMTP:
        return 'SMTP';
      case IMAPSource.IMAP:
        return 'IMAP';
      default:
        return 'Unknown';
    }
  };

  // Helper to format status message
  const getStatusMessage = (
    direction: 'incoming' | 'outgoing',
    source: SMTPSource | IMAPSource,
    email?: string
  ): string => {
    const provider = getProviderName(source);
    const emailPart = email ? `${email} ` : '';
    const directionLabel = direction === 'incoming' ? t('Incoming emails') : t('Outgoing emails');
    
    if (source === SMTPSource.ERRORRED || source === IMAPSource.ERRORRED) {
      return `${directionLabel}: using ${emailPart}over ${provider}: ${t('error')}`;
    } else if (source === SMTPSource.NONE || source === IMAPSource.NONE) {
      return `${directionLabel}: ${t('not configured')}`;
    } else {
      return `${directionLabel}: using ${emailPart}over ${provider}: ${t('connected')}`;
    }
  };

  const [plainOpen, setPlainOpen] = useState(false);
  const [htmlOpen, setHtmlOpen] = useState(false);

  return (
    <Box>
      <Body1 mb={2}>{description || defaultDescription}</Body1>

      {/* Connection Status Alerts */}
      {/* Outgoing (SMTP) Status */}
      {smtpSource !== undefined && smtpSource !== SMTPSource.NONE && (
        <Alert 
          severity={smtpSource === SMTPSource.ERRORRED ? 'error' : 'success'} 
          sx={{ mb: 2 }}
        >
          {getStatusMessage('outgoing', smtpSource, emailAddress)}
        </Alert>
      )}
      {smtpSource === SMTPSource.NONE && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {getStatusMessage('outgoing', smtpSource, emailAddress)}
        </Alert>
      )}

      {/* Incoming (IMAP) Status */}
      {imapSource !== undefined && imapSource !== IMAPSource.NONE && (
        <Alert 
          severity={imapSource === IMAPSource.ERRORRED ? 'error' : 'success'} 
          sx={{ mb: 2 }}
        >
          {getStatusMessage('incoming', imapSource, emailAddress)}
        </Alert>
      )}
      {imapSource === IMAPSource.NONE && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {getStatusMessage('incoming', imapSource, emailAddress)}
        </Alert>
      )}

      {/* OAuth Integration Section */}
      <Alert severity="info" icon={false} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t('Link Email Account')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('Connect your Gmail or Office 365 account to enable email integration using OAuth authentication. This will link your email account using secure token-based authentication.')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <PrimaryButton
            onClick={() => onLinkEmail('gmail')}
          >
            {t('Link Gmail Account')}
          </PrimaryButton>
          <PrimaryButton
            onClick={() => onLinkEmail('office365')}
          >
            {t('Link Office 365 Account')}
          </PrimaryButton>
        </Box>
      </Alert>

      {/* Email Signature Section (deduplicated, modal editing) */}
      {(typeof emailSignaturePlain !== 'undefined' && typeof onChangeEmailSignaturePlain === 'function') && (
        <>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            {t('Email Signature')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('Configure your email signature that will be included in all outgoing emails.')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Plain Signature Preview */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: 120 }}>{t('Plain Text Signature')}</Typography>
              <Typography variant="body2" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', border: '1px solid', borderColor: 'border.default', borderRadius: 1, p: 1, bgcolor: 'background.white' }}>
                {emailSignaturePlain ? emailSignaturePlain : t('No signature set')}
              </Typography>
              <Button variant="outlined" size="small" onClick={() => setPlainOpen(true)}>{t('Edit')}</Button>
            </Box>
            {/* HTML Signature Preview */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: 120 }}>{t('HTML Signature')}</Typography>
              <Box sx={{ flex: 1, border: '1px solid', borderColor: 'border.default', borderRadius: 1, p: 1, bgcolor: 'background.white', minHeight: 32, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {emailSignatureHtml ? <span dangerouslySetInnerHTML={{ __html: emailSignatureHtml }} /> : t('No signature set')}
              </Box>
              <Button variant="outlined" size="small" onClick={() => setHtmlOpen(true)}>{t('Edit')}</Button>
            </Box>
          </Box>

          {/* Plain Signature Modal */}
          <Dialog open={plainOpen} onClose={() => setPlainOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{t('Edit Plain Text Signature')}</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                multiline
                rows={8}
                label={t('Plain Text Signature')}
                name="workingEmailSignaturePlain"
                value={emailSignaturePlain}
                onChange={onChangeEmailSignaturePlain}
                helperText={t('This signature will be used for plain text emails')}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPlainOpen(false)}>{t('Close')}</Button>
            </DialogActions>
          </Dialog>

          {/* HTML Signature Modal */}
          <Dialog open={htmlOpen} onClose={() => setHtmlOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{t('Edit HTML Signature')}</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                multiline
                rows={10}
                label={t('HTML Signature')}
                name="workingEmailSignatureHtml"
                value={emailSignatureHtml}
                onChange={onChangeEmailSignatureHtml}
                helperText={t('This signature will be used for HTML emails. You can use HTML tags for formatting.')}
              />
              {emailSignatureHtml && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    {t('HTML Signature Preview')}
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'border.default',
                      borderRadius: 1,
                      bgcolor: 'background.white',
                    }}
                    dangerouslySetInnerHTML={{ __html: emailSignatureHtml }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setHtmlOpen(false)}>{t('Close')}</Button>
            </DialogActions>
          </Dialog>

        </>
      )}

      {/* Additional content (IMAP/SMTP forms, etc.) */}
      {children}
    </Box>
  );
};

export default EmailIntegration;
